import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  Devices,
  Error,
  PhotoLibrary,
  PlaylistPlay,
  Refresh,
  Schedule,
  Storage,
  Warning,
} from '@mui/icons-material';

import type { DashboardStats } from '../../types';
import apiClient from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

type SeverityColor = 'success' | 'warning' | 'error' | 'info' | 'default';

const numberFormat = new Intl.NumberFormat('ru-RU');

const formatNumber = (value?: number | null) => numberFormat.format(value || 0);

const formatPercent = (value?: number | null) => `${Number(value || 0).toFixed(1)}%`;

const formatDuration = (seconds?: number | null) => {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours} ч ${minutes} мин`;
  if (minutes > 0) return `${minutes} мин`;
  return `${total} сек`;
};

const formatStorageSize = (bytes?: number | null): string => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes || 0;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'нет данных';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'нет данных';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const colorByScore = (score: number): SeverityColor => {
  if (score >= 85) return 'success';
  if (score >= 65) return 'warning';
  return 'error';
};

const levelColor = (level?: string): SeverityColor => {
  if (level === 'error' || level === 'critical') return 'error';
  if (level === 'warning') return 'warning';
  if (level === 'info') return 'info';
  return 'default';
};

const Section: React.FC<{ title: string; right?: React.ReactNode; children: React.ReactNode; dense?: boolean }> = ({
  title,
  right,
  children,
  dense = false,
}) => (
  <Paper elevation={1} sx={{ p: dense ? 1.25 : 1.5, height: '100%', borderRadius: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {title}
      </Typography>
      {right}
    </Stack>
    {children}
  </Paper>
);

const KpiCell: React.FC<{
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color?: SeverityColor;
  caption?: React.ReactNode;
  progress?: number;
}> = ({ label, value, icon, color = 'info', caption, progress }) => (
  <Paper
    elevation={1}
    sx={{
      p: 1.1,
      height: '100%',
      borderRadius: 1.25,
      borderLeft: 4,
      borderColor: `${color}.main`,
      minWidth: 0,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: `${color}.main`, display: 'flex', mt: 0.2 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ lineHeight: 1.15, fontWeight: 900 }}>
          {value}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
            {caption}
          </Typography>
        )}
      </Box>
    </Stack>
    {typeof progress === 'number' && (
      <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, progress))} sx={{ mt: 0.8, height: 4, borderRadius: 1 }} />
    )}
  </Paper>
);

const SignalCell: React.FC<{
  label: string;
  value: React.ReactNode;
  caption: React.ReactNode;
  color?: SeverityColor;
  progress?: number;
}> = ({ label, value, caption, color = 'info', progress }) => {
  const mainColor = color === 'default' ? 'text.secondary' : `${color}.main`;

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 0.9,
        py: 0.75,
        minWidth: 0,
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={0.75}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900, color: mainColor }} noWrap>
          {value}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
        {caption}
      </Typography>
      {typeof progress === 'number' && (
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, progress))}
          sx={{ mt: 0.55, height: 3, borderRadius: 1 }}
        />
      )}
    </Paper>
  );
};

const DistributionBar: React.FC<{ data?: Record<string, number>; labelMap?: Record<string, string> }> = ({ data, labelMap }) => {
  const entries = Object.entries(data || {}).filter(([, value]) => value > 0);
  const total = entries.reduce((sum, [, value]) => sum + value, 0);
  if (!entries.length) {
    return <Typography variant="caption" color="text.secondary">Нет данных</Typography>;
  }

  return (
    <Stack spacing={0.7}>
      {entries.map(([key, value]) => {
        const percentage = total > 0 ? (value / total) * 100 : 0;
        return (
          <Box key={key}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="caption" noWrap>{labelMap?.[key] || key}</Typography>
              <Typography variant="caption" color="text.secondary">{value}</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={percentage} sx={{ height: 5, borderRadius: 1 }} />
          </Box>
        );
      })}
    </Stack>
  );
};

const HourlyBars: React.FC<{ data?: Record<string | number, number> }> = ({ data }) => {
  const values = Array.from({ length: 24 }, (_, index) => Number(data?.[index] || 0));
  const max = Math.max(1, ...values);
  return (
    <Stack direction="row" alignItems="flex-end" spacing={0.35} sx={{ height: 76 }}>
      {values.map((value, index) => (
        <Tooltip key={index} title={`${index}:00 - ${value} событий`} arrow>
          <Box
            sx={{
              flex: 1,
              minWidth: 4,
              height: `${Math.max(6, (value / max) * 72)}px`,
              bgcolor: value ? 'primary.main' : 'action.disabledBackground',
              borderRadius: 0.5,
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const {
    data: stats,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats'],
    queryFn: () => apiClient.getDashboardStats(),
    refetchInterval: 30000,
  });

  const healthScore = stats?.health_score ?? stats?.online_percentage ?? 0;
  const healthColor = colorByScore(healthScore);
  const storagePercent = stats?.storage_usage_percentage ?? (
    stats?.storage_total ? (stats.storage_used / stats.storage_total) * 100 : 0
  );
  const errorState = (stats?.errors_24h || 0) > 0 || (stats?.failed_commands_24h || 0) > 0;
  const commandBacklog = stats?.command_backlog_rate_24h || 0;
  const staleCount = stats?.stale_devices_count ?? stats?.stale_devices?.length ?? 0;
  const contentRotation = stats?.content_rotation_coverage_24h || 0;
  const playbackCoverage = stats?.playback_device_coverage_24h || 0;
  const playlistRotation = stats?.playlist_rotation_coverage_24h || 0;

  if (isLoading && !stats) {
    return (
      <Box>
        <Skeleton width={320} height={42} />
        <Grid container spacing={1.25} sx={{ mt: 1 }}>
          {Array.from({ length: 12 }).map((_, index) => (
            <Grid key={index} size={{ xs: 6, sm: 4, md: 2 }}>
              <Skeleton variant="rounded" height={92} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.25} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            {t('dashboard.title')} Digital Signage
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.full_name || user?.username} · автообновление каждые 30 секунд · сейчас: {new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color={healthColor === 'default' ? 'info' : healthColor}
            icon={errorState ? <Warning /> : <CheckCircle />}
            label={`Health ${formatPercent(healthScore)}`}
          />
          <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
            <Refresh fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1.25 }}>
          Не удалось загрузить dashboard. Проверьте backend `/api/v1/dashboard/stats`.
        </Alert>
      )}

      <Grid container spacing={1.25} sx={{ mb: 1.25 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCell
            label="Устройства"
            value={formatNumber(stats?.total_devices)}
            caption={`${formatNumber(stats?.online_devices)} online · ${formatNumber(stats?.offline_devices)} offline`}
            icon={<Devices fontSize="small" />}
            color={colorByScore(stats?.online_percentage || 0)}
            progress={stats?.online_percentage || 0}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCell
            label="Сейчас играет"
            value={formatNumber(stats?.active_playback_devices)}
            caption={`из ${formatNumber(stats?.total_devices)} экранов`}
            icon={<PlaylistPlay fontSize="small" />}
            color={(stats?.active_playback_devices || 0) > 0 ? 'success' : 'warning'}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCell
            label="Показы 24ч"
            value={formatNumber(stats?.playbacks_24h)}
            caption={`${formatDuration(stats?.playback_duration_24h)} · ${formatPercent(stats?.playback_completion_rate_24h)} complete`}
            icon={<CheckCircle fontSize="small" />}
            color={(stats?.playbacks_24h || 0) > 0 ? 'success' : 'warning'}
            progress={stats?.playback_completion_rate_24h || 0}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCell
            label="Ошибки 24ч"
            value={formatNumber(stats?.errors_24h)}
            caption={`${formatNumber(stats?.warnings_24h)} warning · ${formatPercent(stats?.error_rate_24h)} error rate`}
            icon={<Error fontSize="small" />}
            color={(stats?.errors_24h || 0) > 0 ? 'error' : 'success'}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCell
            label="Команды 24ч"
            value={formatNumber(stats?.commands_24h)}
            caption={`${formatNumber(stats?.pending_commands_24h)} pending · ${formatNumber(stats?.failed_commands_24h)} failed`}
            icon={<Schedule fontSize="small" />}
            color={(stats?.failed_commands_24h || 0) > 0 ? 'error' : 'info'}
            progress={stats?.command_success_rate_24h || 100}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <KpiCell
            label="Хранилище"
            value={formatStorageSize(stats?.storage_used)}
            caption={`${formatPercent(storagePercent)} из ${formatStorageSize(stats?.storage_total)}`}
            icon={<Storage fontSize="small" />}
            color={storagePercent > 85 ? 'error' : storagePercent > 65 ? 'warning' : 'info'}
            progress={storagePercent}
          />
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 1, mb: 1.25, borderRadius: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.85 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            Операционные BI-сигналы
          </Typography>
          <Chip size="small" label="24ч / сейчас" />
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gap: 0.75,
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              xl: 'repeat(8, minmax(0, 1fr))',
            },
          }}
        >
          <SignalCell
            label="Экраны с показами"
            value={`${formatNumber(stats?.unique_playback_devices_24h)}/${formatNumber(stats?.total_devices)}`}
            caption={formatPercent(playbackCoverage)}
            progress={playbackCoverage}
            color={colorByScore(playbackCoverage)}
          />
          <SignalCell
            label="Ротация медиатеки"
            value={`${formatNumber(stats?.unique_media_played_24h)}/${formatNumber(stats?.total_media)}`}
            caption="уникальный контент"
            progress={contentRotation}
            color={contentRotation >= 35 ? 'success' : contentRotation >= 10 ? 'warning' : 'error'}
          />
          <SignalCell
            label="Ротация плейлистов"
            value={`${formatNumber(stats?.unique_playlists_played_24h)}/${formatNumber(stats?.total_playlists)}`}
            caption={formatPercent(playlistRotation)}
            progress={playlistRotation}
            color={playlistRotation >= 50 ? 'success' : playlistRotation >= 20 ? 'warning' : 'error'}
          />
          <SignalCell
            label="Очередь команд"
            value={`${formatNumber(stats?.pending_commands_24h)}/${formatNumber(stats?.commands_24h)}`}
            caption={formatPercent(commandBacklog)}
            progress={commandBacklog}
            color={commandBacklog > 15 ? 'error' : commandBacklog > 5 ? 'warning' : 'success'}
          />
          <SignalCell
            label="Успех команд"
            value={formatPercent(stats?.command_success_rate_24h)}
            caption={`${formatNumber(stats?.failed_commands_24h)} ошибок`}
            progress={stats?.command_success_rate_24h || 0}
            color={colorByScore(stats?.command_success_rate_24h || 0)}
          />
          <SignalCell
            label="Прерывания"
            value={formatNumber(stats?.interrupted_playbacks_24h)}
            caption={`${formatNumber(stats?.playbacks_24h)} показов`}
            color={(stats?.interrupted_playbacks_24h || 0) > 0 ? 'warning' : 'success'}
          />
          <SignalCell
            label="Нет связи"
            value={formatNumber(staleCount)}
            caption={`${formatNumber(stats?.offline_devices)} офлайн`}
            color={staleCount > 0 ? 'warning' : 'success'}
          />
          <SignalCell
            label="Средний плейлист"
            value={stats?.playlist_average_items?.toFixed(1) || '0.0'}
            caption={`${formatNumber(stats?.playlist_items)} элементов`}
            color={(stats?.playlist_average_items || 0) > 0 ? 'info' : 'warning'}
          />
        </Box>
      </Paper>

      <Grid container spacing={1.25}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Section
            title="Операционная сводка"
            right={<Chip size="small" label={`${formatNumber(stats?.events_24h)} событий 24ч`} />}
            dense
          >
            <Grid container spacing={0.8}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Медиа</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(stats?.total_media)} файлов</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Плейлисты</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(stats?.total_playlists)} / {formatNumber(stats?.playlist_items)} элементов</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Расписания</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(stats?.active_schedules)} активных</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">7 дней</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(stats?.playbacks_7d)} показов</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Уникальные экраны 24ч</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(stats?.unique_playback_devices_24h)}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Уникальный контент 24ч</Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>{formatNumber(stats?.unique_media_played_24h)}</Typography>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Section title="Активность по часам" right={<Chip size="small" color="info" label="24ч" />} dense>
            <HourlyBars data={stats?.hourly_activity} />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.6 }}>
              <Typography variant="caption" color="text.secondary">-23ч</Typography>
              <Typography variant="caption" color="text.secondary">сейчас</Typography>
            </Stack>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Section title="Состояние устройств" dense>
            <Grid container spacing={1}>
              <Grid size={6}>
                <DistributionBar data={stats?.device_status_distribution} labelMap={{ online: 'online', offline: 'offline', error: 'error' }} />
              </Grid>
              <Grid size={6}>
                <DistributionBar data={stats?.device_type_distribution} labelMap={{ web_browser: 'web', android_tv: 'android', desktop_pc: 'desktop' }} />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Section title="Последняя активность" right={<Chip size="small" label="реальные activity_logs" />} dense>
            <TableContainer sx={{ maxHeight: 290 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 86 }}>Время</TableCell>
                    <TableCell sx={{ width: 78 }}>Уровень</TableCell>
                    <TableCell sx={{ width: 130 }}>Тип</TableCell>
                    <TableCell>Событие</TableCell>
                    <TableCell sx={{ width: 150 }}>Устройство</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.recent_events || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">Логов активности пока нет.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(stats?.recent_events || []).map((event) => (
                    <TableRow key={event.id} hover>
                      <TableCell>{formatDateTime(event.timestamp)}</TableCell>
                      <TableCell>
                        <Chip size="small" color={levelColor(event.level)} label={event.level} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" noWrap>{event.event_type}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 420 }} noWrap title={event.message}>
                          {event.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {event.device_name || (event.device_id ? `#${event.device_id}` : 'система')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Section title="Проблемные экраны" right={<Chip size="small" color={(stats?.stale_devices || []).length ? 'warning' : 'success'} label={`${formatNumber(stats?.stale_devices?.length)} stale`} />} dense>
            <TableContainer sx={{ maxHeight: 290 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Экран</TableCell>
                    <TableCell>Клиент</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Heartbeat</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.stale_devices || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">Нет stale/offline экранов по текущему порогу.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {(stats?.stale_devices || []).map((device) => (
                    <TableRow key={device.id} hover>
                      <TableCell>
                        <Typography variant="body2" noWrap>{device.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{device.client_kind || 'unknown'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Chip size="small" label={device.status} color={device.status === 'online' ? 'success' : 'warning'} variant="outlined" />
                          {device.playback_status && <Chip size="small" label={device.playback_status} variant="outlined" />}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDateTime(device.last_heartbeat)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Section title="Топ медиа 24ч" dense>
            <Stack spacing={0.8}>
              {(stats?.most_played_media || []).slice(0, 6).map((item) => (
                <Box key={`${item.content_type}-${item.content_id}`}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" noWrap>{item.content_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.total_plays} play</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={Math.min(100, item.completion_rate || 0)} sx={{ height: 4, borderRadius: 1 }} />
                </Box>
              ))}
              {(stats?.most_played_media || []).length === 0 && <Typography variant="caption" color="text.secondary">Нет показов медиа за 24 часа.</Typography>}
            </Stack>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Section title="Топ плейлисты 24ч" dense>
            <Stack spacing={0.8}>
              {(stats?.most_used_playlists || []).slice(0, 6).map((item) => (
                <Box key={`${item.content_type}-${item.content_id}`}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography variant="caption" noWrap>{item.content_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.unique_devices} устройств</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={Math.min(100, item.completion_rate || 0)} sx={{ height: 4, borderRadius: 1 }} />
                </Box>
              ))}
              {(stats?.most_used_playlists || []).length === 0 && <Typography variant="caption" color="text.secondary">Нет показов плейлистов за 24 часа.</Typography>}
            </Stack>
          </Section>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Section title="Контент и события" dense>
            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">Медиа по статусу</Typography>
                <DistributionBar data={stats?.media_status_distribution} />
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary">События по уровню</Typography>
                <DistributionBar data={stats?.event_level_distribution} />
              </Grid>
            </Grid>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
